import React, { useState } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Checkbox,
  Button,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import BasicModal from './BasicModal';
import { Perfil, Acesso, TELAS_DISPONIVEIS } from '../../types/perfil/Perfil';

interface PerfilModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (perfil: Perfil) => Promise<void>;
  perfil: Perfil | null;
  modoEdicao: boolean;
}

const PerfilModal: React.FC<PerfilModalProps> = ({
  open,
  onClose,
  onSave,
  perfil,
  modoEdicao
}) => {
  // Estados do formulário
  const [nome, setNome] = useState(perfil?.nome || '');
  const [descricao, setDescricao] = useState(perfil?.descricao || '');
  const [ativo, setAtivo] = useState(perfil?.ativo ?? true);
  const [acessos, setAcessos] = useState<Acesso[]>(perfil?.acessos || []);
  const [saving, setSaving] = useState(false);
  
  // Estados de controle de UI
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['Analytics', 'Pessoas']);

  // Atualizar estados quando o perfil mudar
  React.useEffect(() => {
    if (perfil) {
      setNome(perfil.nome);
      setDescricao(perfil.descricao || '');
      setAtivo(perfil.ativo);
      
      // Garantir que todas as telas estão representadas nos acessos
      const acessosCompletos: Acesso[] = TELAS_DISPONIVEIS.map(tela => {
        const acessoExistente = perfil.acessos.find(a => a.tela === tela.codigo);
        return acessoExistente || {
          tela: tela.codigo,
          visualizacao: false,
          alteracao: false
        };
      });
      setAcessos(acessosCompletos);
    } else {
      // Novo perfil - inicializar com todas as telas sem permissão
      setNome('');
      setDescricao('');
      setAtivo(true);
      const acessosIniciais: Acesso[] = TELAS_DISPONIVEIS.map(tela => ({
        tela: tela.codigo,
        visualizacao: false,
        alteracao: false
      }));
      setAcessos(acessosIniciais);
    }
  }, [perfil]);

  const handleSave = async () => {
    if (!nome.trim()) {
      return;
    }

    setSaving(true);
    try {
      const perfilData: Perfil = {
        ...(perfil?.guid ? { guid: perfil.guid } : {}),
        nome: nome.trim(),
        descricao: descricao.trim(),
        ativo,
        acessos: acessos.filter(a => a.visualizacao || a.alteracao)
      };

      await onSave(perfilData);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleAcessoChange = (tela: string, tipo: 'visualizacao' | 'alteracao', valor: boolean) => {
    setAcessos(prev => prev.map(acesso => {
      if (acesso.tela === tela) {
        const novoAcesso = { ...acesso, [tipo]: valor };
        
        // Se marcou alteração, automaticamente marca visualização
        if (tipo === 'alteracao' && valor) {
          novoAcesso.visualizacao = true;
        }
        
        // Se desmarcou visualização, automaticamente desmarca alteração
        if (tipo === 'visualizacao' && !valor) {
          novoAcesso.alteracao = false;
        }
        
        return novoAcesso;
      }
      return acesso;
    }));
  };

  const toggleCategoriaExpansion = (categoria: string) => {
    setExpandedCategories(prev => 
      prev.includes(categoria) 
        ? prev.filter(c => c !== categoria)
        : [...prev, categoria]
    );
  };

  const marcarTodosAcessosCategoria = (categoria: string, tipo: 'visualizacao' | 'alteracao', valor: boolean) => {
    const telasDaCategoria = TELAS_DISPONIVEIS.filter(t => t.categoria === categoria);
    
    for (const tela of telasDaCategoria) {
      handleAcessoChange(tela.codigo, tipo, valor);
    }
  };

  const obterAcessoPorTela = (codigoTela: string): Acesso => {
    return acessos.find(a => a.tela === codigoTela) || {
      tela: codigoTela,
      visualizacao: false,
      alteracao: false
    };
  };

  // Agrupar telas por categoria
  const categoriasSet = new Set(TELAS_DISPONIVEIS.map(t => t.categoria));
  const categorias = Array.from(categoriasSet);

  return (
    <BasicModal
      title={modoEdicao ? 'Editar Perfil' : 'Novo Perfil'}
      open={open}
      onClose={onClose}
      saveButton
      onSave={handleSave}
      saveLoading={saving}
      sizeContent="large"
      tela='profile'
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Informações Básicas */}
        <Box>
          <TextField
            fullWidth
            label="Nome do Perfil"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            margin="normal"
            required
            color="secondary"
          />
          
          <TextField
            fullWidth
            label="Descrição"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            margin="normal"
            multiline
            rows={2}
            color="secondary"
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={ativo}
                onChange={(e) => setAtivo(e.target.checked)}
                color="secondary"
              />
            }
            label="Perfil Ativo"
            sx={{ mt: 1 }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Configuração de Acessos */}
        <Typography variant="h6" gutterBottom>
          Configuração de Acessos
        </Typography>

        {categorias.map((categoria) => {
          const telasDaCategoria = TELAS_DISPONIVEIS.filter(t => t.categoria === categoria);
          const isExpanded = expandedCategories.includes(categoria);
          
          return (
            <Accordion 
              key={categoria}
              expanded={isExpanded}
              onChange={() => toggleCategoriaExpansion(categoria)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                  <Typography variant="subtitle1">{categoria}</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        marcarTodosAcessosCategoria(categoria, 'visualizacao', true);
                      }}
                    >
                      Todas Visualização
                    </Button>
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        marcarTodosAcessosCategoria(categoria, 'alteracao', true);
                      }}
                    >
                      Todas Alteração
                    </Button>
                  </Box>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails>
                {telasDaCategoria.map((tela) => {
                  const acesso = obterAcessoPorTela(tela.codigo);
                  
                  return (
                    <Box key={tela.codigo} mb={2}>
                      <Typography variant="body2" fontWeight="medium">
                        {tela.nome}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                        {tela.descricao}
                      </Typography>
                      
                      <Box display="flex" gap={3}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={acesso.visualizacao}
                              onChange={(e) => handleAcessoChange(tela.codigo, 'visualizacao', e.target.checked)}
                              color="secondary"
                            />
                          }
                          label="Visualização"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={acesso.alteracao}
                              onChange={(e) => handleAcessoChange(tela.codigo, 'alteracao', e.target.checked)}
                              color="secondary"
                            />
                          }
                          label="Alteração"
                        />
                      </Box>
                    </Box>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </BasicModal>
  );
};

export default PerfilModal;
